import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { executeQuery } from '../../graphql';
import { validatePlateNumber } from '../../graphql/queries';
import Wizard from '../../components/Wizard';
import InfoBox from '../../components/InfoBox';
import Button from '../../components/Button';
import Recommendor from '../../components/Recommendor';
import { usePageContent } from '../../hooks/usePageContent';
import styles from './index.module.css';

const CreateComboPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { find } = usePageContent('create-combo');
  const wizardContent  = find('wizard')      || {};
  const content        = find('comboChecker') || {};

  const urlParams = new URLSearchParams(location.search);
  const initialCombo = urlParams.get('combination') || '';
  const initialVehicleType = urlParams.get('vehicleType') || 'car';

  const [vehicleType, setVehicleType] = useState(initialVehicleType);
  const [combo, setCombo] = useState(initialCombo);
  const [loading, setLoading] = useState(false);
  const [searchReturned, setSearchReturned] = useState(false);
  const [success, setSuccess] = useState(false);
  const [failedTest, setFailedTest] = useState('');
  const [comboError, setComboError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get('combination')) {
      setCombo('');
      setVehicleType('car');
      setSearchReturned(false);
      setSuccess(false);
      setFailedTest('');
      setComboError('');
    }
    sessionStorage.removeItem('plateOrderData');
  }, [location]);

  const isCar      = vehicleType === 'car';
  const maxLength  = isCar ? (content.maxCharsCar || 6) : (content.maxCharsMotorcycle || 5);
  const minLength  = content.minChars || 2;
  const placeholder   = isCar ? (content.placeholderCar || 'ABC456') : (content.placeholderMotorcycle || 'MOTO5');
  const instructions  = isCar ? (content.instructionsCar || 'Try 2 to 6 letters and/or a combination of letters and number') : (content.instructionsMotorcycle || 'Try 2 to 5 letters and/or a combination of letters and number');
  const comboErrorText = isCar ? (content.comboErrorCar || 'Enter between 2 and 6 letters/numbers only') : (content.comboErrorMotorcycle || 'Enter between 2 and 5 letters/numbers only');

  const infoInitial     = content.infoInitial     || {};
  const infoFormatError = content.infoFormatError || {};
  const infoUnavailable = content.infoUnavailable || {};
  const infoAvailable   = content.infoAvailable   || {};

  const handleVehicleTypeChange = (type) => {
    setVehicleType(type);

    // Re-validate the current combo against the NEW vehicle type's constraints
    const newIsCar = type === 'car';
    const newMaxLength = newIsCar ? (content.maxCharsCar || 6) : (content.maxCharsMotorcycle || 5);
    const newComboErrorText = newIsCar ? (content.comboErrorCar || 'Enter between 2 and 6 letters/numbers only') : (content.comboErrorMotorcycle || 'Enter between 2 and 5 letters/numbers only');
    const regex = /^[0-9a-zA-Z]*$/;

    if (combo.length > 0 && (!regex.test(combo) || combo.length < minLength || combo.length > newMaxLength)) {
      setFailedTest('format');
      setComboError(newComboErrorText);
      setSearchReturned(false);
      setSuccess(false);
    } else if (failedTest === 'availability') {
      // Preserve unavailable state — the combo was already checked and is not available.
      // User must still see the error message and "Try another combo" button.
    } else {
      setFailedTest('');
      setComboError('');
      setSearchReturned(false);
      setSuccess(false);
    }
  };

  const validateComboInput = (value) => {
    const regex = /^[0-9a-zA-Z]*$/;
    if (!regex.test(value)) {
      setComboError(comboErrorText);
      setFailedTest('format');
      return false;
    }
    setComboError('');
    setFailedTest('');
    return true;
  };

  const handleComboChange = (e) => {
    const value = e.target.value.toUpperCase();
    if (value.length <= maxLength) {
      setCombo(value);
      setSearchReturned(false);
      setSuccess(false);
      if (value.length > 0) {
        validateComboInput(value);
      } else {
        setComboError('');
        setFailedTest('');
      }
    }
  };

  const checkComboAvailability = async () => {
    if (combo.length < minLength || combo.length > maxLength || failedTest === 'format') {
      return;
    }

    setLoading(true);
    setSearchReturned(false);

    try {
      const vehicleTypeId = vehicleType === 'car' ? 1 : 2;
      const result = await executeQuery(validatePlateNumber, {
        PlateCombo: combo,
        VehicleType: vehicleTypeId,
        SalesChannel: 1
      });

      if (result.getPlateRuleEngineStatus) {
        const status = result.getPlateRuleEngineStatus.Status;
        setSearchReturned(true);

        if (status === 1) {
          setSuccess(true);
          setFailedTest('');
        } else {
          setSuccess(false);
          setFailedTest('availability');
        }
      }
    } catch (error) {
      setSearchReturned(true);
      setSuccess(false);
      setFailedTest('availability');
    } finally {
      setLoading(false);
    }
  };

  const resetCombo = () => {
    setCombo('');
    setSearchReturned(false);
    setSuccess(false);
    setFailedTest('');
    setComboError('');
    sessionStorage.removeItem('plateOrderData');
  };

  const handleSelectRecommendation = (recommendedCombo) => {
    sessionStorage.setItem('plateOrderData', JSON.stringify({ combination: recommendedCombo, vehicleType }));
    // Replace the current history entry so the browser back button returns here with the combo pre-filled
    const backUrl = `/create/check-combination?combination=${encodeURIComponent(recommendedCombo)}&vehicleType=${encodeURIComponent(vehicleType)}`;
    window.history.replaceState(null, '', backUrl);
    navigate('/create/select-a-style');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (success) {
      sessionStorage.setItem('plateOrderData', JSON.stringify({ combination: combo, vehicleType }));
      // Replace the current history entry so the browser back button returns here with the combo pre-filled
      const backUrl = `/create/check-combination?combination=${encodeURIComponent(combo)}&vehicleType=${encodeURIComponent(vehicleType)}`;
      window.history.replaceState(null, '', backUrl);
      navigate('/create/select-a-style');
    }
  };

  const wizardSteps = wizardContent.steps || [
    { title: 'Check combination' },
    { title: 'Select a style' },
    { title: 'Confirm personal details' },
    { title: 'Checkout' },
  ];

  return (
    <div id="content" className={styles.page}>
      <div className="row">
        <div className="page-header page-header--small">
          <div className="page-header__content">
            <h1 className="page-header__title">{content.pageTitle || 'Create a plate'}</h1>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="plate-wizard">
          {/* Progress Stepper */}
          <Wizard
            steps={wizardSteps}
            currentStep={0}
          />

          {/* Form Page: Combo */}
          <div className="plate-wizard__form plate-wizard__form--combo">
            <div className="plate-wizard__form-header">
              <h2 className="plate-wizard__form-title" tabIndex="-1">
                {content.sectionTitle || 'Check combination'}
              </h2>
            </div>

            <div className="check-combo">
              <form
                action="/create/select-a-style"
                method="get"
                autoComplete="off"
                className={`combination-checker ${loading ? 'loading' : ''}`}
                onSubmit={handleSubmit}
              >
                {/* Loading Panel */}
                {loading && <div className="loading__panel">Loading...</div>}

                <div className="combination-checker__container-inner combination-checker__container-inner--ready">
                  <div className="combination-checker__columns">
                    {/* Vehicle Type Column */}
                    <div className="combination-checker__column combination-checker__column--vehicle">
                      <fieldset
                        className="combination-checker__graphic-radio"
                        disabled={loading || (searchReturned && success)}
                      >
                        <legend className="combination-checker__label">
                          {content.vehicleTypeLabel || 'Select your vehicle type'}
                        </legend>
                        <div className="combination-checker__graphic-radio-options">
                          <div className={`combination-checker__graphic-radio-option${searchReturned && vehicleType !== 'car' ? ' combination-checker__graphic-radio-option--greyed' : ''}`}>
                            <input
                              className="combination-checker__graphic-radio-option-input visuallyhidden visuallyhidden--no-focus"
                              type="radio"
                              name="wizard-check__type"
                              id="wizard-check__type-car"
                              value="car"
                              checked={vehicleType === 'car'}
                              onChange={() => handleVehicleTypeChange('car')}
                            />
                            <label
                              className="combination-checker__graphic-radio-option-label"
                              htmlFor="wizard-check__type-car"
                            >
                              <span className="combination-checker__graphic-radio-option-label-text">
                                {content.carLabel || 'Car'}
                              </span>
                              <span
                                className="combination-checker__graphic-radio-option-label-image-holder"
                                aria-hidden="true"
                              >
                                <img
                                  className="combination-checker__graphic-radio-option-label-image combination-checker__graphic-radio-option-label-image--car"
                                  src="/images/icons/car--tree-green.svg"
                                  alt=""
                                />
                              </span>
                            </label>
                          </div>
                          <div className={`combination-checker__graphic-radio-option${searchReturned && vehicleType !== 'motorcycle' ? ' combination-checker__graphic-radio-option--greyed' : ''}`}>
                            <input
                              className="combination-checker__graphic-radio-option-input visuallyhidden visuallyhidden--no-focus"
                              type="radio"
                              name="wizard-check__type"
                              id="wizard-check__type-motorcycle"
                              value="motorcycle"
                              checked={vehicleType === 'motorcycle'}
                              onChange={() => handleVehicleTypeChange('motorcycle')}

                            />
                            <label
                              className="combination-checker__graphic-radio-option-label"
                              htmlFor="wizard-check__type-motorcycle"
                            >
                              <span className="combination-checker__graphic-radio-option-label-text">
                                {content.motorcycleLabel || 'Motorbike'}
                              </span>
                              <span
                                className="combination-checker__graphic-radio-option-label-image-holder"
                                aria-hidden="true"
                              >
                                <img
                                  className="combination-checker__graphic-radio-option-label-image combination-checker__graphic-radio-option-label-image--motorcycle"
                                  src="/images/icons/motorcycle--tree-green.svg"
                                  alt=""
                                />
                              </span>
                            </label>
                          </div>
                        </div>
                      </fieldset>
                      <input type="hidden" value={vehicleType} name="vehicleType" />
                    </div>

                    {/* Info Column */}
                    <div className="combination-checker__column combination-checker__column--info" tabIndex="-1">
                      {/* Initial Info Box */}
                      {!searchReturned && failedTest !== 'format' && (
                        <InfoBox
                          title={infoInitial.title || 'Suggested plate combinations'}
                          icon="info"
                          className="info-box--max-450"
                        >
                          <p>Here&apos;s a few examples:</p>
                          {infoInitial.examples?.length > 0 && (
                            <ul>{infoInitial.examples.map((e) => <li key={e}>{e}</li>)}</ul>
                          )}
                          {infoInitial.tips?.map((tip, i) => <p key={i}>{tip}</p>)}
                        </InfoBox>
                      )}

                      {/* Format Error Info Box */}
                      {!success && failedTest === 'format' && (
                        <InfoBox
                          title={infoFormatError.title || 'This combination is not valid.'}
                          icon="error"
                          className="info-box--max-450"
                        >
                          {infoFormatError.intro && <p>{infoFormatError.intro}</p>}
                          {infoFormatError.exampleLabel && <p>{infoFormatError.exampleLabel}</p>}
                          {infoFormatError.examples?.length > 0 && (
                            <ul>{infoFormatError.examples.map((e) => <li key={e}>{e}</li>)}</ul>
                          )}
                        </InfoBox>
                      )}

                      {/* Unavailable Info Box */}
                      {searchReturned && !success && failedTest === 'availability' && (
                        <InfoBox
                          title={"Sorry! " + (infoUnavailable.title || "This combo's not available")}
                          icon="error"
                          className="info-box--max-450"
                        >
                          <p>{infoUnavailable.intro || 'Either someone beat you to it or it\'s on our "naughty list".'}</p>
                          {infoUnavailable.exampleLabel && <p>{infoUnavailable.exampleLabel}</p>}
                          {infoUnavailable.examples?.length > 0 && (
                            <ul>{infoUnavailable.examples.map((e) => <li key={e}>{e}</li>)}</ul>
                          )}
                          {infoUnavailable.tips?.map((tip, i) => <p key={i}>{tip}</p>)}
                          <p>Keep in mind, some styles have certain combo rules. If you have a look in mind, start by checking out the styles first, to see which combo&apos;s work in that style.</p>
                        </InfoBox>
                      )}

                      {/* Available Info Box */}
                      {searchReturned && success && (
                        <InfoBox
                          title={infoAvailable.title || "Your plate combo's available!"}
                          icon="success"
                          className="info-box--max-450"
                        >
                          {infoAvailable.description && <p>{infoAvailable.description}</p>}
                        </InfoBox>
                      )}
                    </div>

                    {/* Combo Input Column */}
                    <div className="combination-checker__column combination-checker__column--combo">
                      <h3 className="combination-checker__label" aria-hidden="true">
                        {content.comboInputLabel || 'Enter your plate combo'}
                      </h3>
                      <div className="combination-checker__combo">
                        <div
                          className={`combination-checker__combo-text ${
                            failedTest === 'format' || success ? 'visuallyhidden' : ''
                          }`}
                        >
                          <label
                            className="combination-checker__label combination-checker__combo-text-instructions"
                            htmlFor="combination"
                          >
                            <span className="visuallyhidden">Your plate combination. </span>
                            <span aria-hidden={loading || (searchReturned && success)}>
                              {instructions}
                            </span>
                          </label>
                          <div className="combination-checker__combo-text-count" id="combination-checker__count" aria-hidden="true">
                            <h3 className="visuallyhidden">Character count</h3>
                            {combo.length}/{maxLength}
                          </div>
                        </div>
                        <div
                          className={`combination-checker__combo-container ${
                            searchReturned && success ? 'combination-checker__combo-container--ticked' : ''
                          }`}
                        >
                          <input
                            readOnly={searchReturned && success}
                            disabled={loading}
                            className="combination-checker__combo-input"
                            value={combo}
                            id="combination"
                            name="combination"
                            type="text"
                            spellCheck="false"
                            onChange={handleComboChange}
                            placeholder={placeholder}
                            maxLength={maxLength}
                          />
                        </div>
                        {comboError && (
                          <div className="combination-checker__combo-error" role="alert">
                            <p className="combination-checker__combo-error-msg">{comboError}</p>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="combination-checker__actions">
                        {(!searchReturned || (searchReturned && failedTest === 'format')) && (
                          <Button
                            type="button"
                            fullWidth
                            onClick={checkComboAvailability}
                            disabled={loading || failedTest !== '' || combo.length < minLength}
                            loading={loading}
                          >
                            {content.checkButtonText || 'Check availability'}
                          </Button>
                        )}

                        {searchReturned && success && (
                          <Button
                            type="submit"
                            fullWidth
                            disabled={!success || combo.length < minLength}
                          >
                            {content.chooseStyleButtonText || 'Choose your plate style'}
                          </Button>
                        )}

                        {((searchReturned && success) || (initialCombo && combo)) && (
                          <Button
                            type="button"
                            variant="tertiary"
                            className="button--icon-right button--tertiary-grass"
                            onClick={resetCombo}
                            disabled={loading}
                          >
                            {content.resetButtonText || 'Reset'}
                          </Button>
                        )}

                        {searchReturned && !success && failedTest === 'availability' && (
                          <Button
                            type="button"
                            fullWidth
                            className="button--try-another"
                            onClick={resetCombo}
                            disabled={loading}
                          >
                            Try another combo
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Recommendor */}
                  {searchReturned && !success && failedTest === 'availability' && combo.length >= minLength && (
                    <Recommendor
                      originalCombo={combo}
                      vehicleType={vehicleType}
                      onSelectRecommendation={handleSelectRecommendation}
                      maxLength={maxLength}
                    />
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateComboPage;
